import React, { useState, useEffect } from 'react';
import QRCode from 'qrcode';
import { generatePaymentURI } from '../utils/payment-uri';

interface QRPaymentProps {
  network: string;
  address: string;
  amount?: string;
  amountBaseUnits?: string;
  chainId?: number;
  tokenAddress?: string;
  tokenSymbol?: string;
  decimals?: number;
  onClose?: () => void;
}

const QRPayment: React.FC<QRPaymentProps> = ({
  network,
  address,
  amount,
  amountBaseUnits,
  chainId,
  tokenAddress,
  tokenSymbol,
  decimals,
  onClose
}) => {
  const [qrDataUrl, setQrDataUrl] = useState<string>('');
  const [paymentUri, setPaymentUri] = useState<string>('');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    generateQRCode();
  }, [network, address, amount, chainId, tokenAddress, decimals]);

  const generateQRCode = async () => {
    try {
      setError(''); // Clear any previous errors
      
      // Validate inputs
      if (!network || !address || (!amount && !amountBaseUnits)) {
        setError('Missing required payment information');
        return;
      }
      
      // Generate payment URI
      const uri = generatePaymentURI({
        network,
        address,
        amount,
        amountBaseUnits,
        chainId,
        tokenAddress,
        decimals,
        label: 'Token Presale',
        message: 'Purchase tokens'
      });

      if (!uri) {
        setError(`Unable to generate payment URI for ${network} network. Please use manual entry.`);
        return;
      }

      setPaymentUri(uri);

      // Generate QR code with error handling
      try {
        const dataUrl = await QRCode.toDataURL(uri, {
          width: 300,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          },
          errorCorrectionLevel: 'M'
        });

        setQrDataUrl(dataUrl);
        setError('');
      } catch (qrError) {
        console.error('QR code generation failed:', qrError);
        setError('Failed to generate QR code. You can still copy the payment URI below.');
        // Keep the URI available even if QR generation fails
      }
      
    } catch (err) {
      console.error('Error in generateQRCode:', err);
      setError('Failed to prepare payment information. Please try again.');
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(paymentUri);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      // Fallback: Try to select the text for manual copy
      try {
        const textArea = document.createElement('textarea');
        textArea.value = paymentUri;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (fallbackErr) {
        console.error('Fallback copy also failed:', fallbackErr);
        // Could show a toast or alert here
      }
    }
  };

  const openInWallet = () => {
    // For mobile devices, attempt to open the URI directly
    // This will trigger the appropriate wallet app if installed
    if (/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
      window.location.href = paymentUri;
    } else {
      // On desktop, open in a new tab (wallet extensions may intercept)
      window.open(paymentUri, '_blank');
    }
  };

  // Format display address for readability
  const formatAddress = (addr: string) => {
    if (addr.length <= 10) return addr;
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  // Get payment instructions based on network
  const getInstructions = () => {
    switch (network) {
      case 'Ethereum':
      case 'Arbitrum':
      case 'Base':
      case 'Polygon':
      case 'BNB':
      case 'Avalanche':
        return 'Scan this QR code with your wallet app to complete the payment';
      case 'Solana':
        return 'Scan with a Solana wallet like Phantom or Solflare';
      case 'Bitcoin':
        return 'Scan with your Bitcoin wallet to send the payment';
      case 'Tron':
      case 'Polkadot':
        return 'Manually enter the payment details shown below in your wallet';
      default:
        return 'Scan this QR code or copy the payment details';
    }
  };

  return (
    <div className="qr-payment-modal">
      <div className="qr-payment-content">
        <button
          onClick={onClose}
          className="close-button"
          aria-label="Close QR payment modal"
        >
          ✕
        </button>

        <h2 className="qr-title">Complete Payment</h2>
        
        <p className="qr-instructions">{getInstructions()}</p>

        {error ? (
          <div className="error-message">{error}</div>
        ) : (
          <>
            <div className="qr-container">
              {qrDataUrl && (
                <img 
                  src={qrDataUrl} 
                  alt="Payment QR Code" 
                  className="qr-code"
                />
              )}
            </div>

            <div className="payment-details">
              <div className="detail-row">
                <span className="detail-label">Network:</span>
                <span className="detail-value">{network}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">To:</span>
                <span className="detail-value">{formatAddress(address)}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Amount:</span>
                <span className="detail-value">
                  {amount ? `${amount} ${tokenSymbol || 'crypto'}` : 'Amount provided by wallet'}
                </span>
              </div>
              {tokenAddress && (
                <div className="detail-row">
                  <span className="detail-label">Token:</span>
                  <span className="detail-value">{tokenSymbol}</span>
                </div>
              )}
            </div>

            <div className="action-buttons">
              <button
                onClick={copyToClipboard}
                className="action-button copy-button"
              >
                {copied ? '✓ Copied!' : '📋 Copy URI'}
              </button>
              
              <button
                onClick={openInWallet}
                className="action-button wallet-button"
              >
                🔗 Open in Wallet
              </button>
            </div>

            {/* Show raw URI for manual entry if needed */}
            {(network === 'Tron' || network === 'Polkadot') && (
              <div className="manual-entry">
                <p className="manual-label">Payment Details (for manual entry):</p>
                <pre className="payment-json">{paymentUri}</pre>
              </div>
            )}
          </>
        )}
      </div>

      <style jsx>{`
        .qr-payment-modal {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 1rem;
        }

        .qr-payment-content {
          background: var(--bg-primary, #fff);
          border-radius: 1rem;
          padding: 2rem;
          max-width: 450px;
          width: 100%;
          position: relative;
          max-height: 90vh;
          overflow-y: auto;
        }

        .close-button {
          position: absolute;
          top: 1rem;
          right: 1rem;
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          color: var(--text-secondary, #666);
          width: 2rem;
          height: 2rem;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 0.5rem;
          transition: all 0.2s;
        }

        .close-button:hover {
          background: var(--bg-secondary, #f3f4f6);
          color: var(--text-primary, #111);
        }

        .qr-title {
          font-size: 1.5rem;
          font-weight: 700;
          margin-bottom: 0.5rem;
          text-align: center;
          color: var(--text-primary, #111);
        }

        .qr-instructions {
          text-align: center;
          color: var(--text-secondary, #666);
          margin-bottom: 1.5rem;
          font-size: 0.875rem;
        }

        .error-message {
          background: var(--error-light, #fee);
          color: var(--error-color, #dc2626);
          padding: 1rem;
          border-radius: 0.5rem;
          text-align: center;
          margin-bottom: 1rem;
        }

        .qr-container {
          display: flex;
          justify-content: center;
          margin-bottom: 1.5rem;
          padding: 1.5rem;
          background: white;
          border-radius: 0.75rem;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
        }

        .qr-code {
          width: 300px;
          height: 300px;
          image-rendering: pixelated;
        }

        .payment-details {
          background: var(--bg-secondary, #f9fafb);
          padding: 1rem;
          border-radius: 0.5rem;
          margin-bottom: 1.5rem;
        }

        .detail-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.5rem 0;
          border-bottom: 1px solid var(--border-color, #e5e7eb);
        }

        .detail-row:last-child {
          border-bottom: none;
        }

        .detail-label {
          font-size: 0.875rem;
          color: var(--text-secondary, #666);
        }

        .detail-value {
          font-size: 0.875rem;
          font-weight: 600;
          color: var(--text-primary, #111);
          font-family: monospace;
        }

        .action-buttons {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
        }

        .action-button {
          padding: 0.75rem 1rem;
          border: none;
          border-radius: 0.5rem;
          font-size: 0.875rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
        }

        .copy-button {
          background: var(--bg-secondary, #f3f4f6);
          color: var(--text-primary, #111);
        }

        .copy-button:hover {
          background: var(--bg-tertiary, #e5e7eb);
        }

        .wallet-button {
          background: var(--accent-color, #6366f1);
          color: white;
        }

        .wallet-button:hover {
          background: var(--accent-dark, #4f46e5);
        }

        .manual-entry {
          margin-top: 1.5rem;
          padding-top: 1.5rem;
          border-top: 1px solid var(--border-color, #e5e7eb);
        }

        .manual-label {
          font-size: 0.875rem;
          color: var(--text-secondary, #666);
          margin-bottom: 0.5rem;
        }

        .payment-json {
          background: var(--bg-secondary, #f9fafb);
          padding: 1rem;
          border-radius: 0.5rem;
          font-size: 0.75rem;
          font-family: monospace;
          word-break: break-all;
          white-space: pre-wrap;
          max-height: 150px;
          overflow-y: auto;
        }

        @media (max-width: 480px) {
          .qr-payment-content {
            padding: 1.5rem;
          }

          .qr-code {
            width: 250px;
            height: 250px;
          }

          .action-buttons {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default QRPayment;